import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { buildApiUrl, getAuthHeaders } from '../../utils/apiBase';
import { Modal } from "../ui/modal";

export default function SettingsInstantPayments() {
    const [providers, setProviders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Config modals
    const [configProvider, setConfigProvider] = useState<string | null>(null);

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl('/settings/online-payment/instant-payments'), {
                headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
            });
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setProviders(data.data || []);
        } catch (err) {
            console.error('Failed to fetch instant payments:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    const toggleProvider = async (id: number, currentActive: boolean) => {
        const newActive = !currentActive;
        try {
            const res = await fetch(buildApiUrl('/settings/online-payment/instant-payments/toggle'), {
                method: 'POST',
                headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ gateway_id: id, active: newActive }),
            });
            if (!res.ok) throw new Error('Failed to toggle');
            
            setProviders(providers.map(p => p.id === id ? { ...p, active: newActive } : p));
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Updated successfully', showConfirmButton: false, timer: 2000 });
        } catch (err) {
            console.error('Failed to toggle provider:', err);
            Swal.fire({ title: 'Error', text: 'Failed to update status', icon: 'error' });
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Instant Payment Providers</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Enable direct payment buttons (like PayPal or Amazon Pay) for quick checkout.
                    </p>
                </div>
                {loading && <span className="text-sm text-gray-400">Loading...</span>}
            </div>

            <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {providers.map(provider => (
                        <div key={provider.id} className="flex flex-col gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-800 dark:text-white leading-tight">{provider.label || provider.name}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{provider.description}</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer" 
                                        checked={!!provider.active}
                                        onChange={() => toggleProvider(provider.id, !!provider.active)}
                                    />
                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-brand-500"></div>
                                </label>
                            </div>
                            
                            <div className="mt-2 pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                                <button
                                    onClick={() => setConfigProvider(provider.slug)}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Configure Credentials
                                </button>
                            </div>
                        </div>
                    ))}
                    {providers.length === 0 && !loading && (
                        <div className="col-span-full py-8 text-center text-gray-500 text-sm">
                            No instant payment providers available.
                        </div>
                    )}
                </div>
            </div>

            {/* Config Modals */}
            <Modal isOpen={!!configProvider} onClose={() => setConfigProvider(null)} className="max-w-2xl p-0 overflow-hidden bg-gray-50 dark:bg-gray-900">
                <div className="p-6">
                    {configProvider === 'paypal' && (
                        <div>
                            {/* We will build SettingsPayPal.tsx next if it doesn't exist, or just put the form here */}
                            <SettingsPayPal />
                        </div>
                    )}
                    {configProvider === 'payfort' && (
                        <div>
                            {/* We already have SettingsAmazon, but that is for Amazon SP-API. Payfort has its own backend endpoints. Let's create a SettingsPayfort or put the form here. */}
                            <SettingsPayfort />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function SettingsPayPal() {
    const [formData, setFormData] = useState({
        paypal_sandbox_mode: true,
        paypal_sandbox_client_id: '',
        paypal_sandbox_client_secret: '',
        paypal_live_client_id: '',
        paypal_live_client_secret: '',
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        fetch(buildApiUrl('/settings/paypal-settings'), { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(data => {
                const s = data.data || {};
                setFormData({
                    paypal_sandbox_mode: s.paypal_sandbox_mode ?? true,
                    paypal_sandbox_client_id: s.paypal_sandbox_client_id || '',
                    paypal_sandbox_client_secret: s.paypal_sandbox_client_secret || '',
                    paypal_live_client_id: s.paypal_live_client_id || '',
                    paypal_live_client_secret: s.paypal_live_client_secret || '',
                });
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await fetch(buildApiUrl('/settings/paypal-settings/update'), {
                method: 'POST',
                headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(formData),
            });
            Swal.fire({ title: 'Success', text: 'PayPal settings saved', icon: 'success' });
        } catch (err) {
            Swal.fire({ title: 'Error', text: 'Failed to save', icon: 'error' });
        }
    };

    return (
        <div>
            <h3 className="text-lg font-bold mb-4">PayPal Credentials</h3>
            {loading ? <p>Loading...</p> : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.paypal_sandbox_mode} onChange={e => setFormData({...formData, paypal_sandbox_mode: e.target.checked})} className="rounded text-brand-500" />
                        <span className="text-sm">Sandbox Mode</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm mb-1">Sandbox Client ID</label>
                            <input type="text" value={formData.paypal_sandbox_client_id} onChange={e => setFormData({...formData, paypal_sandbox_client_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Sandbox Secret</label>
                            <input type="password" value={formData.paypal_sandbox_client_secret} onChange={e => setFormData({...formData, paypal_sandbox_client_secret: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm mb-1">Live Client ID</label>
                            <input type="text" value={formData.paypal_live_client_id} onChange={e => setFormData({...formData, paypal_live_client_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Live Secret</label>
                            <input type="password" value={formData.paypal_live_client_secret} onChange={e => setFormData({...formData, paypal_live_client_secret: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" />
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600">Save</button>
                    </div>
                </form>
            )}
        </div>
    );
}

function SettingsPayfort() {
    const [formData, setFormData] = useState({
        payfort_sandbox_mode: true,
        payfort_merchant_identifier: '',
        payfort_access_code: '',
        payfort_sha_request_phrase: '',
        payfort_response_phrase: '',
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        fetch(buildApiUrl('/settings/payfort-settings'), { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(data => {
                const s = data.data || {};
                // Since backend returns 1 or 0 for presence, we might not get actual values, 
                // but let's assume standard behavior for now. If it's a 1/0 we just show empty inputs to be filled.
                setFormData({
                    payfort_sandbox_mode: s.PAYFORT_SANDBOX_MODE ?? true,
                    payfort_merchant_identifier: '',
                    payfort_access_code: '',
                    payfort_sha_request_phrase: '',
                    payfort_response_phrase: '',
                });
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await fetch(buildApiUrl('/settings/payfort-settings/update'), {
                method: 'POST',
                headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(formData),
            });
            Swal.fire({ title: 'Success', text: 'Amazon Pay (Payfort) settings saved', icon: 'success' });
        } catch (err) {
            Swal.fire({ title: 'Error', text: 'Failed to save', icon: 'error' });
        }
    };

    return (
        <div>
            <h3 className="text-lg font-bold mb-4">Amazon Pay (Payfort) Credentials</h3>
            {loading ? <p>Loading...</p> : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.payfort_sandbox_mode} onChange={e => setFormData({...formData, payfort_sandbox_mode: e.target.checked})} className="rounded text-brand-500" />
                        <span className="text-sm">Sandbox Mode</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm mb-1">Merchant Identifier</label>
                            <input type="text" value={formData.payfort_merchant_identifier} onChange={e => setFormData({...formData, payfort_merchant_identifier: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" placeholder="Enter new value to update" />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Access Code</label>
                            <input type="password" value={formData.payfort_access_code} onChange={e => setFormData({...formData, payfort_access_code: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" placeholder="Enter new value to update" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm mb-1">SHA Request Phrase</label>
                            <input type="password" value={formData.payfort_sha_request_phrase} onChange={e => setFormData({...formData, payfort_sha_request_phrase: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" placeholder="Enter new value to update" />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Response Phrase</label>
                            <input type="password" value={formData.payfort_response_phrase} onChange={e => setFormData({...formData, payfort_response_phrase: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800" placeholder="Enter new value to update" />
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600">Save</button>
                    </div>
                </form>
            )}
        </div>
    );
}
